import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useSortedPaginated, SortableTh, Pagination, useFilters, FilterRow } from '../components/TableControls';
import { formatDate } from '../utils/dateUtils';
import useAutoSelectSingle from '../hooks/useAutoSelectSingle';
import InvalidateWarningModal from '../components/InvalidateWarningModal';

export default function Sales() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [form, setForm] = useState({ store_id: '', item_id: '', quantity: '', date: new Date().toISOString().slice(0, 10) });
  const [msg, setMsg] = useState('');
  const [msgErr, setMsgErr] = useState(false);
  const [editTxn, setEditTxn] = useState(null);
  const [dupConfirm, setDupConfirm] = useState(null);
  const [invalidate, setInvalidate] = useState(null);
  const { filters, setFilter, filtered } = useFilters(transactions, ['store_name', 'item_name', 'user_name']);
  const { paginated, sortKey, sortDir, setSort, page, setPage, perPage, setPerPage, total } = useSortedPaginated(filtered, 'date', 'desc');

  const loadTxns = () => api.get('/transactions', { params: { transaction_type: 'sale' } }).then(r => setTransactions(r.data));

  useEffect(() => {
    api.get('/stores').then(r => setStores(r.data));
    api.get('/items').then(r => setItems(r.data));
    loadTxns();
  }, []);

  useAutoSelectSingle(stores, form.store_id, (id) => setForm(f => ({ ...f, store_id: id })));
  useAutoSelectSingle(items, form.item_id, (id) => setForm(f => ({ ...f, item_id: id })));

  const doSubmit = async (force = false) => {
    setMsg(''); setMsgErr(false);
    try {
      await api.post(`/transactions/sale${force ? '?force=true' : ''}`, {
        store_id: parseInt(form.store_id),
        item_id: parseInt(form.item_id),
        quantity: parseInt(form.quantity, 10),
        date: form.date,
      });
      setMsg('¡Venta registrada!');
      setForm({ ...form, item_id: '', quantity: '' });
      loadTxns();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => doSubmit(true) });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error'); setMsgErr(true);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const dup = transactions.find(
      t => t.store_id === parseInt(form.store_id) &&
           t.item_id === parseInt(form.item_id) &&
           t.date === form.date
    );
    if (dup) {
      const itemName = items.find(i => i.id === parseInt(form.item_id))?.name || dup.item_name;
      const storeName = stores.find(s => s.id === parseInt(form.store_id))?.name || dup.store_name;
      setDupConfirm({ dup, itemName, storeName, newQty: parseInt(form.quantity, 10) });
      return;
    }
    await doSubmit();
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`¿Eliminar esta venta de ${t.quantity} x ${t.item_name}?`)) return;
    doDelete(t, false);
  };

  const doDelete = async (t, force) => {
    setMsg(''); setMsgErr(false);
    try {
      const res = await api.delete(`/transactions/${t.id}${force ? '?force=true' : ''}`);
      setMsg(res.data.message);
      loadTxns();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => doDelete(t, true) });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error'); setMsgErr(true);
    }
  };

  const openEdit = (t) => setEditTxn({ id: t.id, quantity: t.quantity, date: t.date, label: `${t.item_name} @ ${t.store_name}` });

  const saveEdit = (e) => { e.preventDefault(); doSaveEdit(false); };

  const doSaveEdit = async (force) => {
    setMsg(''); setMsgErr(false);
    try {
      await api.put(`/transactions/${editTxn.id}${force ? '?force=true' : ''}`, {
        quantity: parseInt(editTxn.quantity, 10),
        date: editTxn.date,
      });
      setMsg('¡Venta actualizada!');
      setEditTxn(null);
      loadTxns();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => doSaveEdit(true) });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error'); setMsgErr(true);
    }
  };

  return (
    <div>
      <div className="page-header"><h1>Ventas</h1></div>

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Registrar Venta</h3>
        {msg && <div className={`alert ${msgErr ? 'alert-danger' : 'alert-success'}`}>{msg}</div>}
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            <div className="form-group">
              <label>Tienda</label>
              <select value={form.store_id} onChange={e => setForm({...form, store_id: e.target.value})} required>
                <option value="">Seleccionar tienda</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Artículo</label>
              <select value={form.item_id} onChange={e => setForm({...form, item_id: e.target.value})} required>
                <option value="">Seleccionar artículo</option>
                {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Cantidad</label>
              <input type="number" step="1" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Fecha</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required />
            </div>
          </div>
          <button type="submit" className="btn btn-primary">Registrar Venta</button>
        </form>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Ventas Recientes</h3>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <SortableTh colKey="date" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Fecha</SortableTh>
                <SortableTh colKey="store_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Tienda</SortableTh>
                <SortableTh colKey="item_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Artículo</SortableTh>
                <SortableTh colKey="quantity" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Cant.</SortableTh>
                <SortableTh colKey="user_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Por</SortableTh>
                {(user?.role === 'admin' || user?.role === 'manager') && <th>Acciones</th>}
              </tr>
              <FilterRow
                columns={[null, 'store_name', 'item_name', null, 'user_name', ...(user?.role === 'admin' || user?.role === 'manager' ? [null] : [])]}
                data={transactions}
                filters={filters}
                setFilter={setFilter}
              />
            </thead>
            <tbody>
              {paginated.map(t => (
                <tr key={t.id}>
                  <td>{formatDate(t.date)}</td><td>{t.store_name}</td><td>{t.item_name}</td>
                  <td>{t.quantity}</td><td>{t.user_name}</td>
                  {(user?.role === 'admin' || user?.role === 'manager') && (
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(t)}>Editar</button>
                      {' '}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(t)}>Eliminar</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
      </div>

      {dupConfirm && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Venta Duplicada Detectada</h2>
            <p style={{ marginBottom: 8 }}>
              Ya se registró una venta de <strong>{dupConfirm.dup.quantity} × {dupConfirm.itemName}</strong> en <strong>{dupConfirm.storeName}</strong> el <strong>{dupConfirm.dup.date}</strong>.
            </p>
            <p style={{ marginBottom: 16, color: 'var(--text-2)' }}>
              Agregar <strong>{dupConfirm.newQty}</strong> más llevará el total del día a <strong>{dupConfirm.dup.quantity + dupConfirm.newQty}</strong> unidades. Continúa solo si esta es una venta separada e intencional.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDupConfirm(null)}>Cancelar</button>
              <button className="btn btn-warning" onClick={async () => { setDupConfirm(null); await doSubmit(); }}>Continuar de todas formas</button>
            </div>
          </div>
        </div>
      )}

      {editTxn && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Editar Venta</h2>
            <p style={{ color: 'var(--text-2)', marginBottom: 12 }}>{editTxn.label}</p>
            <form onSubmit={saveEdit}>
              <div className="form-group">
                <label>Cantidad</label>
                <input type="number" step="1" min="1" value={editTxn.quantity} onChange={e => setEditTxn({...editTxn, quantity: e.target.value})} required autoFocus />
              </div>
              <div className="form-group">
                <label>Fecha</label>
                <input type="date" value={editTxn.date} onChange={e => setEditTxn({...editTxn, date: e.target.value})} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditTxn(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <InvalidateWarningModal
        data={invalidate}
        onCancel={() => setInvalidate(null)}
        onConfirm={() => { const r = invalidate.run; setInvalidate(null); r(); }}
      />
    </div>
  );
}
